import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useGetCrmDataQuery } from 'generated/graphql';

import { getCreatorApiUrl } from '@client/components/api-urls';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';
import {
  BundlePricing,
  BusinessModel,
} from '@playbook-technologies/common-enums';

import { FilterOption } from '../filter-option';
import { sortBy, uniq } from 'lodash';

type Bundle = {
  title: string;
  pricing: BundlePricing;
  subscriptionTier: SubscriptionTier;
};

enum BillingCycleInterval {
  Month = 'month',
  Year = 'year',
}

interface SubscriptionTier {
  id: number;
  providerProductId: string;
  name: string;
  prices: SubscriptionTierPrice[];
}

interface SubscriptionTierPrice {
  id: number;
  providerPriceId: string;
  billingCycleInterval: BillingCycleInterval;
  billingCycleIntervalUnit: number;
  price: number;
}

const fetchBundles = async (creatorId: number): Promise<Bundle[]> => {
  const url = new URL(getCreatorApiUrl('/bundles'));

  const response = await axios.get<Bundle[]>(url.toString(), {
    params: {
      influencerId: creatorId,
      includeArchivedPrices: true,
    },
  });

  return response.data;
};

const PRODUCTS_BY_VISIBILITY: { [key: string]: FilterOption[] } = {
  premium: [
    { id: 'Premium - Monthly', label: 'Premium - Monthly' },
    { id: 'Premium - Annual', label: 'Premium - Annual' },
  ],
  exclusive: [
    { id: 'Exclusive - Monthly', label: 'Exclusive - Monthly' },
    { id: 'Exclusive - Annual', label: 'Exclusive - Annual' },
  ],
  limited: [
    { id: 'Limited - Monthly', label: 'Limited - Monthly' },
    { id: 'Limited - Annual', label: 'Limited - Annual' },
    { id: 'Premium - Monthly', label: 'Premium - Monthly' },
    { id: 'Premium - Annual', label: 'Premium - Annual' },
  ],
};

export const useProductOptions = (): FilterOption[] => {
  const creatorId = useInfluencerId();
  const result = useGetCrmDataQuery({
    variables: {
      creatorId,
    },
  });
  const { isFetched, data: bundles } = useQuery(['bundles', creatorId], () =>
    fetchBundles(creatorId),
  );

  const visibility = result.data?.creator?.visibility;
  const businessModel = result.data?.creator?.businessModel;

  if (businessModel === BusinessModel.Bundle) {
    if (isFetched && bundles) {
      const products = bundles
        .filter((b) => b.pricing === BundlePricing.SUBSCRIPTION)
        .flatMap((b) =>
          b.subscriptionTier.prices.map((p) => {
            const cycle =
              p.billingCycleInterval === BillingCycleInterval.Month
                ? 'Monthly'
                : 'Annual';
            return {
              price: p.price,
              label: `${b.title} - ${cycle} - $${(p.price / 100).toFixed(2)}`,
            };
          }),
        );

      return uniq(sortBy(products, (p) => p.price).map((p) => p.label)).map(
        (label) => ({
          id: label,
          label: label,
        }),
      );
    } else {
      return [];
    }
  }

  return visibility ? PRODUCTS_BY_VISIBILITY[visibility] ?? [] : [];
};
